/**
 * Small, local multisamples for the melody and bass voices. Audio stays out of
 * the initial bundle and is decoded once per context when a voice is selected.
 * Sources and licences: public/instruments/musescore-general/README.md (the
 * tenor sax and trumpet, from MuseScore_General / FluidR3, MIT) and
 * public/instruments/generaluser/README.md (the electric bass, GeneralUser GS).
 * `scripts/extract-sf2-instrument.mjs` reproduces either set from its SoundFont.
 * The jazz guitar and the upright bass are not zone tables: their recordings
 * (public/instruments/archtop/, public/instruments/upright/) are velocity-
 * layered one-shots played through src/audio/layeredSampler.ts, like the piano.
 */
export type LeadSound = 'tenor-sax' | 'trumpet' | 'guitar' | 'piano';
export type GuitarSound = 'jazz-guitar' | 'original';
export type BassSound = 'upright' | 'electric' | 'original';
export type SampledInstrumentSound =
  | Exclude<LeadSound, 'guitar' | 'piano'>
  | Exclude<BassSound, 'original' | 'upright'>;
export type InstrumentSound = LeadSound | BassSound | GuitarSound;

export interface InstrumentArticulation {
  /** Linear amplitude, multiplied by the player's note velocity / channel gain. */
  gain: number;
  /** Short anti-click fade; the recording already contains the natural attack. */
  attackSeconds: number;
  decaySeconds: number;
  /** Linear amplitude relative to the attack peak, held until note-off. */
  sustainLevel: number;
  releaseSeconds: number;
  filterHz: number;
}

export interface InstrumentSampleDefinition extends InstrumentArticulation {
  file: string;
  keyLow: number;
  keyHigh: number;
  /** Pitch of the stored waveform, which can differ from its intended zone. */
  rootMidi: number;
  /** SoundFont pitch correction: positive cents raise playback pitch. */
  tuneCents: number;
  sampleRate: number;
  frames: number;
  loopStartFrame: number;
  loopEndFrame: number;
}

export interface InstrumentSample extends InstrumentSampleDefinition {
  buffer: AudioBuffer;
  /** Seconds in the decoded buffer, before playback-rate transposition. */
  loopStart: number;
  loopEnd: number;
  /**
   * Where the recording's attack is heard, seconds into the buffer (before
   * transposition): the player starts the sample this much early so the note
   * SOUNDS on its beat (docs/swing-plan.md §4.3). The tenor's breath takes
   * about 25 ms to speak; without this every head landed that late. Absent
   * (a sample built elsewhere) is heard from its start.
   */
  onsetSeconds?: number;
}

export interface InstrumentSampleSet {
  sound: SampledInstrumentSound;
  samples: readonly InstrumentSample[];
}

export interface SelectedInstrumentSample {
  sample: InstrumentSample;
  playbackRate: number;
}

// NB a zone's own gain REPLACES this one (see `definitions`), so the level
// lives in the zone table: each gain is 0.125 / the loop's RMS (recorded in
// provenance.json), which balances the registers AND sets the lead's place
// in the mix. That target was measured on the master bus in a real browser
// (George's Dilemma, the head alone vs each channel alone): at the old
// GeneralUser gains (0.17–0.37 on loops of RMS 0.15–0.32, product ≈ 0.055)
// the tenor sat at the comp's RMS and 5 dB under the bass — "really weedy"
// (user, Sep 2026); at 0.125 it sits level with the bass and 5–8 dB over the
// comp, where a lead reads as a lead. The recordings are FluidR3's tenor via
// MuseScore_General (Sep 2026, user: "can we get a better tenor sax
// sample?"): fifteen zones a semitone or two apart from B3 to D♯6, each 1.5–3
// seconds of real attack and body before a ~0.23 s sustain loop set ~85% of
// the way in, so a note shorter than a bar plays the recording and only a
// held note ever loops — the GeneralUser set was eight loops of 0.07–0.24 s
// with no attack of their own. The lowpass opens to 6.5 kHz (the files are
// 32 kHz) so the reed's edge is in the sound.
const SAX: InstrumentArticulation = {
  gain: 0.5,
  attackSeconds: 0.004,
  decaySeconds: 0.2,
  sustainLevel: 0.88,
  releaseSeconds: 0.055,
  filterHz: 6500,
};

// Trumpet recordings include the tongued attack and breath/body before looping.
// Match the tenor's 0.125 loop RMS; avoid an added reed-style vibrato.
const TRUMPET: InstrumentArticulation = {
  gain: 0.5,
  attackSeconds: 0.003,
  decaySeconds: 0.16,
  sustainLevel: 0.9,
  releaseSeconds: 0.055,
  filterHz: 7000,
};

const ELECTRIC: InstrumentArticulation = {
  gain: 0.4,
  attackSeconds: 0.003,
  decaySeconds: 2.8,
  sustainLevel: 0.12,
  releaseSeconds: 0.09,
  filterHz: 900,
};

type Zone = readonly [
  sourceIndex: number,
  keyLow: number,
  keyHigh: number,
  rootMidi: number,
  tuneCents: number,
  sampleRate: number,
  frames: number,
  loopStartFrame: number,
  loopEndFrame: number,
  gain: number,
];

function definitions(
  sound: SampledInstrumentSound,
  folder: 'musescore-general' | 'generaluser',
  articulation: InstrumentArticulation,
  zones: readonly Zone[],
): readonly InstrumentSampleDefinition[] {
  return zones.map(
    ([
      sourceIndex,
      keyLow,
      keyHigh,
      rootMidi,
      tuneCents,
      sampleRate,
      frames,
      start,
      end,
      gain,
    ]) => ({
      ...articulation,
      file: `${folder}/${sound}-${sourceIndex}.wav`,
      keyLow,
      keyHigh,
      rootMidi,
      tuneCents,
      sampleRate,
      frames,
      loopStartFrame: start,
      loopEndFrame: end,
      gain,
    }),
  );
}

/**
 * Preserve the original instrument zones, not just nearest waveform roots.
 * Several source samples were stored faster to save space; for example the
 * fingered low E's waveform root is F#3, although it serves the low E1 zone.
 * Loop points and tuning are copied from the source SoundFont sample headers.
 * End zones extend to MIDI's bounds so transposed charts remain playable.
 */
export const INSTRUMENT_SAMPLE_DEFINITIONS: Readonly<
  Record<SampledInstrumentSound, readonly InstrumentSampleDefinition[]>
> = {
  'tenor-sax': definitions('tenor-sax', 'musescore-general', SAX, [
    [371, 0, 47, 47, 0, 32000, 59850, 52484, 59757, 0.41],
    [369, 48, 49, 49, 0, 32000, 71437, 63798, 71187, 0.43],
    [367, 50, 51, 51, -5, 32000, 55879, 48435, 55821, 0.42],
    [365, 52, 53, 52, -10, 32000, 49738, 42874, 49633, 0.38],
    [363, 54, 55, 55, -5, 32000, 65620, 58042, 65528, 0.38],
    [361, 56, 57, 56, -4, 32000, 69713, 61913, 69606, 0.39],
    [359, 58, 59, 58, -2, 32000, 75105, 66853, 75077, 0.41],
    [322, 60, 61, 60, -15, 32000, 58168, 50730, 58129, 0.4],
    [357, 62, 63, 62, -13, 32000, 95574, 87615, 95520, 0.32],
    [320, 64, 65, 65, -8, 32000, 89583, 82333, 89533, 0.52],
    [355, 66, 67, 66, -13, 32000, 78391, 71375, 78323, 0.68],
    [353, 68, 69, 69, -6, 32000, 69237, 61594, 69190, 0.38],
    [351, 70, 71, 71, -3, 32000, 81456, 73510, 81351, 0.57],
    [349, 72, 73, 73, -2, 32000, 92434, 84624, 92348, 0.55],
    [318, 74, 127, 75, 0, 32000, 69064, 60813, 68987, 0.57],
  ]),
  trumpet: definitions('trumpet', 'musescore-general', TRUMPET, [
    [644, 0, 66, 64, 0, 44100, 18354, 7185, 18296, 0.728],
    [765, 67, 71, 67, 0, 44100, 16822, 8026, 16794, 0.534],
    [764, 72, 75, 72, 0, 44100, 23224, 14270, 23202, 0.588],
    [763, 76, 83, 79, 0, 44100, 19888, 10629, 19860, 0.497],
    [762, 84, 90, 88, 0, 44100, 18583, 11853, 18555, 0.542],
    [761, 91, 127, 96, 0, 44100, 18938, 9248, 18910, 0.5],
  ]),
  electric: definitions('electric', 'generaluser', ELECTRIC, [
    [230, 0, 30, 54, 10, 44100, 10549, 10300, 10540, 0.42],
    [228, 31, 35, 60, 13, 44100, 9818, 9617, 9787, 0.38],
    [229, 36, 40, 64, 27, 44100, 10373, 10229, 10365, 0.37],
    [231, 41, 127, 69, 29, 44100, 10261, 10149, 10251, 0.34],
  ]),
};

interface CacheEntry {
  loading: Promise<InstrumentSampleSet | null>;
  ready: InstrumentSampleSet | null;
}

/** Where a recorded note is heard as starting: how long its attack takes to
 *  reach half its peak (the perceptual onset lies near there), searched over
 *  the first 150 ms in 1 ms steps. 0 for a silent or unreadable buffer. */
export function perceptualOnsetSeconds(buffer: {
  sampleRate: number;
  numberOfChannels: number;
  length: number;
  getChannelData(channel: number): Float32Array;
}): number {
  if (typeof buffer.getChannelData !== 'function') return 0;
  const step = Math.max(1, Math.round(buffer.sampleRate / 1000));
  const frames = Math.min(buffer.length, Math.round(buffer.sampleRate * 0.15));
  if (!(frames > 0) || !(buffer.numberOfChannels > 0)) return 0;
  const channels = Array.from({ length: buffer.numberOfChannels }, (_, c) =>
    buffer.getChannelData(c),
  );
  const envelope: number[] = [];
  for (let start = 0; start < frames; start += step) {
    let level = 0;
    for (const data of channels) {
      for (let i = start; i < Math.min(frames, start + step); i++) {
        level = Math.max(level, Math.abs(data[i]));
      }
    }
    envelope.push(level);
  }
  const peak = Math.max(...envelope);
  if (!(peak > 0)) return 0;
  const at = envelope.findIndex((level) => level >= peak / 2);
  return at <= 0 ? 0 : (at * step) / buffer.sampleRate;
}

const contexts = new WeakMap<BaseAudioContext, Map<string, CacheEntry>>();

function deploymentBase(baseUrl: string): string {
  return baseUrl === '' || baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
}

/** URL honors Vite's deployment prefix and works without a runtime CDN. */
export function instrumentSampleURL(
  sample: InstrumentSampleDefinition,
  baseUrl = import.meta.env.BASE_URL,
): string {
  return `${deploymentBase(baseUrl)}instruments/${sample.file}`;
}

/** Synchronous scheduler lookup: it never starts a request or waits for audio. */
export function getInstrumentSamples(
  context: BaseAudioContext,
  sound: InstrumentSound,
  baseUrl = import.meta.env.BASE_URL,
): InstrumentSampleSet | null {
  return contexts.get(context)?.get(`${deploymentBase(baseUrl)}:${sound}`)?.ready ?? null;
}

/**
 * Load only the requested instrument. Concurrent calls share their work.
 * Failure resolves to null so the existing synthesized voice can answer, and
 * stays cached until the context is replaced (audio reset). Repeated editor
 * previews must not launch another set of failed requests while offline. A
 * partially available instrument uses its surviving nearest zones.
 */
export function loadInstrumentSamples(
  context: BaseAudioContext,
  sound: InstrumentSound,
  baseUrl = import.meta.env.BASE_URL,
): Promise<InstrumentSampleSet | null> {
  // The original guitar/bass are synthesised; the piano, the jazz guitar and
  // the upright are layered one-shots (src/audio/layeredSampler.ts) — none is
  // a set here.
  if (
    sound === 'guitar' ||
    sound === 'original' ||
    sound === 'piano' ||
    sound === 'jazz-guitar' ||
    sound === 'upright'
  ) {
    return Promise.resolve(null);
  }
  const specs = INSTRUMENT_SAMPLE_DEFINITIONS[sound];
  if (!specs) return Promise.resolve(null);
  let cache = contexts.get(context);
  if (!cache) {
    cache = new Map();
    contexts.set(context, cache);
  }
  const key = `${deploymentBase(baseUrl)}:${sound}`;
  const existing = cache.get(key);
  if (existing) return existing.loading;

  const entry: CacheEntry = { loading: Promise.resolve(null), ready: null };
  cache.set(key, entry);
  entry.loading = Promise.all(
    specs.map(async (spec): Promise<InstrumentSample | null> => {
      try {
        const response = await fetch(instrumentSampleURL(spec, baseUrl));
        if (!response.ok) return null;
        const buffer = await context.decodeAudioData(await response.arrayBuffer());
        const loopStart = spec.loopStartFrame / spec.sampleRate;
        const loopEnd = spec.loopEndFrame / spec.sampleRate;
        // Decoding can resample to the AudioContext rate. Loop seconds still
        // agree, while source frame indexes would no longer address the loop.
        // Reject truncated/invalid files rather than loop silence or throw in
        // the real-time scheduler. A one-frame rounding tolerance is enough.
        const tolerance = 1 / buffer.sampleRate;
        if (
          !Number.isFinite(buffer.duration) ||
          !Number.isFinite(tolerance) ||
          buffer.sampleRate <= 0 ||
          buffer.duration <= 0 ||
          loopStart < 0 ||
          loopEnd <= loopStart ||
          loopEnd > buffer.duration + tolerance
        ) {
          return null;
        }
        return {
          ...spec,
          buffer,
          loopStart,
          loopEnd: Math.min(loopEnd, buffer.duration),
          onsetSeconds: perceptualOnsetSeconds(buffer),
        };
      } catch {
        return null;
      }
    }),
  ).then((decoded) => {
    const samples = decoded.filter((sample): sample is InstrumentSample => sample !== null);
    entry.ready = samples.length ? { sound, samples } : null;
    return entry.ready;
  });
  return entry.loading;
}

/** Choose the authored zone, or its nearest survivor after a partial load. */
export function selectInstrumentSample(
  set: InstrumentSampleSet | null,
  midi: number,
): SelectedInstrumentSample | null {
  if (!set || !Number.isFinite(midi) || midi < 0 || midi > 127) return null;
  let best: InstrumentSample | null = null;
  let bestDistance = Infinity;
  for (const sample of set.samples) {
    const distance = Math.max(sample.keyLow - midi, midi - sample.keyHigh, 0);
    if (distance < bestDistance) {
      best = sample;
      bestDistance = distance;
    }
  }
  if (!best) return null;
  const playbackRate = 2 ** ((midi - best.rootMidi + best.tuneCents / 100) / 12);
  return { sample: best, playbackRate };
}
